// src/pages/Reports/WeekdayEconomicsReportPage.js
//
// Weekday Economics Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents weekday-level economic analytics based on completed and
// checked visits.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Which weekdays generate the most revenue?
// - Which weekdays are strongest per open day?
// - Which weekdays produce the best revenue per booked hour?
// - Which weekdays consume the most booked time?
// - Which weekdays dominate visits and business mix?
// - Which weekdays are commercially strong but operationally soft?
// - Which weekdays deserve stronger staffing, promotion, or schedule review?
//
// WHY THIS REPORT IS IMPORTANT
// ----------------------------
// Slot Performance answers:
//   "Which hours are strong?"
//
// Category Economics answers:
//   "Which business segments are strong?"
//
// Weekday Economics answers:
//   "Which days of the week are strong?"
//
// This makes it highly useful for management decisions around:
// - opening hours
// - promotions by weekday
// - staffing intensity
// - protecting premium days
// - improving weaker weekdays
// - understanding the weekly business rhythm of the salon
//
// BACKEND CONTRACT EXPECTED BY THIS VERSION
// -----------------------------------------
// This frontend version assumes the backend already returns the explicit KPI pair:
//
// {
//   period,
//   filters,
//   methodology,
//   opening_schedule,
//   summary,
//   rows
// }
//
// Row shape expected:
//
// {
//   weekday,
//   weekday_label,
//   is_open,
//   open_days_count,
//   opening_minutes_per_day,
//   theoretical_open_minutes_in_period,
//
//   total_visits,
//   total_booked_minutes,
//   total_revenue,
//
//   avg_revenue_per_visit,
//   avg_duration_minutes,
//   revenue_per_booked_hour,
//
//   avg_revenue_per_open_day,
//   avg_booked_minutes_per_open_day,
//   avg_visits_per_open_day,
//
//   team_utilization_vs_opening_time,
//   single_stream_opening_coverage,
//
//   share_of_total_revenue,
//   share_of_total_booked_minutes,
//   share_of_total_visits,
//
//   top_categories,
//   top_services
// }
//
// IMPORTANT KPI INTERPRETATION
// ----------------------------
// 1) team_utilization_vs_opening_time
//    - compares total booked team minutes against opening minutes
//    - may exceed 100%
//    - useful for understanding total team load intensity
//
// 2) single_stream_opening_coverage
//    - capped at 100%
//    - tells whether one continuous stream of work could cover the opening time
//    - useful for interpreting whether the salon was "fully covered" before
//      discussing parallel overlap
//
// This pair solves the old ambiguity of values such as 138%.
//
// ARCHITECTURAL PRINCIPLES
// ------------------------
// - Business calculations remain backend-owned.
// - Frontend only derives chart-ready and interpretation-ready views.
// - Local sorting/search remain page-owned because they are fast and transparent.
// - Tooltip and insight helpers may build managerial language,
//   but must not silently replace backend truth.
// - The page remains visually aligned with the other report screens.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchWeekdayEconomicsReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./WeekdayEconomicsReportPage.css";

// I18N NOTE
// ---------
// This page keeps backend KPI fields, weekday identifiers, executive class
// codes, chart data keys, and calculations stable. The React layer translates
// frontend-owned headings, labels, chart series names, local executive insights,
// tooltips, empty states, and fallback strings. Backend methodology
// note_keys are translated here, while legacy notes remain a fallback.
// Weekday labels remain backend payload content.

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Convert input into a finite number or a fallback.
 *
 * Why this helper matters:
 * - backend payloads may still arrive as strings
 * - charts are much safer when we normalize once
 * - it avoids repeated Number.isFinite boilerplate
 */
function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Format money values consistently.
 */
function formatMoney(value) {
  return toFiniteNumber(value, 0).toFixed(2);
}

/**
 * Format minute values with one decimal.
 */
function formatMinutes(value) {
  return toFiniteNumber(value, 0).toFixed(1);
}

/**
 * Format integer-like counts.
 */
function formatCount(value) {
  return String(Math.round(toFiniteNumber(value, 0)));
}

/**
 * Generic decimal formatter.
 */
function formatNumber(value) {
  return toFiniteNumber(value, 0).toFixed(2);
}

/**
 * Format a ratio like:
 * - 0.42  -> 42.0%
 * - 1.38  -> 138.0%
 */
function formatPercentFromRatio(value) {
  return `${(toFiniteNumber(value, 0) * 100).toFixed(1)}%`;
}

/**
 * Format a value already expressed as "percent-like number".
 * Example:
 * - 72.3 -> 72.3%
 */
function formatPercent(value) {
  return `${toFiniteNumber(value, 0).toFixed(1)}%`;
}

/**
 * Render compact top-list arrays from backend.
 */
function formatTopList(items, t) {
  if (!Array.isArray(items) || items.length === 0) return "—";
  return items
    .map((x) =>
      t("reports:weekdayEconomics.topList.item", {
        name: x.name,
        count: x.count,
      })
    )
    .join(", ");
}

/* -------------------------------------------------------------------------- */
/* Chart palette                                                              */
/* -------------------------------------------------------------------------- */

const CHART_COLORS = Object.freeze({
  revenue: "#2563eb",
  revenueLight: "#60a5fa",
  efficiency: "#16a34a",
  efficiencyLight: "#4ade80",
  fill: "#7c3aed",
  cumulative: "#f59e0b",
  weak: "#f97316",
  poor: "#dc2626",
  neutral: "#94a3b8",
  grid: "#e5e7eb",
  axis: "#6b7280",
});

/* -------------------------------------------------------------------------- */
/* KPI access helpers                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Explicit KPI accessor.
 *
 * This version intentionally assumes the backend delta has already been applied.
 * So we do NOT silently fall back to the old booked_time_fill_rate here.
 */
function getTeamUtilizationRatio(row) {
  return toFiniteNumber(row?.team_utilization_vs_opening_time, 0);
}

/**
 * Explicit capped coverage KPI accessor.
 *
 * This should normally be present once backend delta is live.
 * We still keep a null-safe display path in UI so the page degrades gracefully
 * if one environment is temporarily behind, but this helper itself assumes
 * the new contract.
 */
function getSingleStreamCoverageRatio(row) {
  const value = row?.single_stream_opening_coverage;
  if (value == null || value === "") return null;
  return toFiniteNumber(value, 0);
}

/* -------------------------------------------------------------------------- */
/* Executive classification helpers                                           */
/* -------------------------------------------------------------------------- */

/**
 * Bubble size helper.
 *
 * We intentionally compress range because there are only a few weekday points.
 * If the range is too wide, one weekday dominates visually too aggressively.
 */
function bubbleSizeFromValue(value, minValue, maxValue) {
  const v = toFiniteNumber(value, 0);
  const min = toFiniteNumber(minValue, 0);
  const max = toFiniteNumber(maxValue, 0);

  if (max <= min) return 160;

  const ratio = (v - min) / (max - min);
  return 110 + ratio * 260;
}

/**
 * Assign a fast managerial class to a weekday.
 *
 * This is intentionally heuristic:
 * - it is designed for executive readability
 * - not for accounting or mathematical truth
 */
function getWeekdayExecutiveClass(row) {
  const totalRevenue = toFiniteNumber(row?.total_revenue, 0);
  const revenuePerBookedHour = toFiniteNumber(row?.revenue_per_booked_hour, 0);
  const teamUtilization = getTeamUtilizationRatio(row);
  const avgRevenuePerOpenDay = toFiniteNumber(row?.avg_revenue_per_open_day, 0);

  if (
    totalRevenue >= 1500 &&
    revenuePerBookedHour >= 130 &&
    teamUtilization >= 0.45
  ) {
    return "star";
  }

  if (revenuePerBookedHour >= 145 && avgRevenuePerOpenDay >= 250) {
    return "efficient";
  }

  if (totalRevenue >= 1500 && teamUtilization < 0.35) {
    return "volume";
  }

  if (avgRevenuePerOpenDay < 150 && teamUtilization < 0.28) {
    return "weak";
  }

  return "standard";
}

function getWeekdayExecutiveColor(executiveClass) {
  switch (executiveClass) {
    case "star":
      return CHART_COLORS.efficiency;
    case "efficient":
      return "#0ea5e9";
    case "volume":
      return CHART_COLORS.cumulative;
    case "weak":
      return CHART_COLORS.poor;
    case "standard":
    default:
      return CHART_COLORS.revenue;
  }
}

function getExecutiveClassExplanation(executiveClass, t) {
  switch (executiveClass) {
    case "star":
      return t("reports:weekdayEconomics.executiveClassExplanations.star");
    case "efficient":
      return t("reports:weekdayEconomics.executiveClassExplanations.efficient");
    case "volume":
      return t("reports:weekdayEconomics.executiveClassExplanations.volume");
    case "weak":
      return t("reports:weekdayEconomics.executiveClassExplanations.weak");
    case "standard":
    default:
      return t("reports:weekdayEconomics.executiveClassExplanations.standard");
  }
}

/* -------------------------------------------------------------------------- */
/* Executive interpretation helpers                                           */
/* -------------------------------------------------------------------------- */

function buildWeeklyRhythmInterpretation(row, t) {
  const avgRevenue = toFiniteNumber(row?.avg_revenue_per_open_day, 0);
  const teamUtilization = getTeamUtilizationRatio(row);
  const revenuePerHour = toFiniteNumber(row?.revenue_per_booked_hour, 0);
  const singleStreamCoverage = getSingleStreamCoverageRatio(row);

  if (
    singleStreamCoverage != null &&
    singleStreamCoverage >= 0.95 &&
    teamUtilization > 1.1
  ) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.deepDemand");
  }

  if (avgRevenue >= 1700 && teamUtilization >= 0.45) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.veryStrong");
  }

  if (avgRevenue >= 1500 && revenuePerHour >= 145) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.strongEconomic");
  }

  if (avgRevenue >= 1500 && teamUtilization < 0.30) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.highValueSoftUtilization");
  }

  if (avgRevenue < 1200 && teamUtilization < 0.28) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.commerciallyWeak");
  }

  if (revenuePerHour >= 150 && avgRevenue < 1400) {
    return t("reports:weekdayEconomics.weeklyRhythmInterpretations.efficientSmaller");
  }

  return t("reports:weekdayEconomics.weeklyRhythmInterpretations.balanced");
}

function buildRevenueConcentrationInterpretation(paretoRows, t) {
  if (!Array.isArray(paretoRows) || paretoRows.length === 0) {
    return t("reports:weekdayEconomics.revenueConcentrationInterpretations.empty");
  }

  const top1 = paretoRows[0];
  const top2 = paretoRows[1];
  const top3 = paretoRows[2];

  const top1Share = toFiniteNumber(top1?.share_of_total_revenue, 0);
  const top2Cum = toFiniteNumber(top2?.cumulative_revenue_share_percent, 0);
  const top3Cum = toFiniteNumber(top3?.cumulative_revenue_share_percent, 0);

  if (top1Share >= 0.28) {
    return t("reports:weekdayEconomics.revenueConcentrationInterpretations.topOne", {
      weekday: top1?.label,
    });
  }

  if (top2Cum >= 65) {
    return t("reports:weekdayEconomics.revenueConcentrationInterpretations.topTwo");
  }

  if (top3Cum >= 80) {
    return t("reports:weekdayEconomics.revenueConcentrationInterpretations.topThree");
  }

  return t("reports:weekdayEconomics.revenueConcentrationInterpretations.spread");
}

/* -------------------------------------------------------------------------- */
/* Driver / drilldown helpers                                                 */
/* -------------------------------------------------------------------------- */

function buildUtilizationHeadline(row, t) {
  return t("reports:weekdayEconomics.driver.utilizationHeadline", {
    value: formatPercentFromRatio(getTeamUtilizationRatio(row)),
  });
}

function buildSingleStreamCoverageHeadline(row, t) {
  const ratio = getSingleStreamCoverageRatio(row);
  if (ratio == null) {
    return t("reports:weekdayEconomics.driver.singleStreamCoverageHeadlineNA");
  }
  return t("reports:weekdayEconomics.driver.singleStreamCoverageHeadline", {
    value: formatPercentFromRatio(ratio),
  });
}

function buildUtilizationMeaning(row, t) {
  const teamUtilization = getTeamUtilizationRatio(row);
  const singleStreamCoverage = getSingleStreamCoverageRatio(row);
  const revenuePerHour = toFiniteNumber(row?.revenue_per_booked_hour, 0);
  const avgRevenuePerOpenDay = toFiniteNumber(row?.avg_revenue_per_open_day, 0);

  if (
    singleStreamCoverage != null &&
    singleStreamCoverage >= 0.95 &&
    teamUtilization > 1.1
  ) {
    return t("reports:weekdayEconomics.driver.meanings.deepTeamDemand");
  }

  if (teamUtilization >= 1.2 && revenuePerHour >= 140) {
    return t("reports:weekdayEconomics.driver.meanings.highRevenueParallelUsage");
  }

  if (teamUtilization >= 1.0 && revenuePerHour < 120) {
    return t("reports:weekdayEconomics.driver.meanings.heavilyLoadedSofterEconomics");
  }

  if (teamUtilization >= 0.6 && avgRevenuePerOpenDay >= 1500) {
    return t("reports:weekdayEconomics.driver.meanings.goodValueSolidUtilization");
  }

  if (teamUtilization < 0.35 && avgRevenuePerOpenDay >= 1500) {
    return t("reports:weekdayEconomics.driver.meanings.goodValueSoftUtilization");
  }

  if (teamUtilization < 0.3 && avgRevenuePerOpenDay < 1200) {
    return t("reports:weekdayEconomics.driver.meanings.softCommercialOperational");
  }

  return t("reports:weekdayEconomics.driver.meanings.middleRange");
}

function buildUtilizationAction(row, t) {
  const teamUtilization = getTeamUtilizationRatio(row);
  const revenuePerHour = toFiniteNumber(row?.revenue_per_booked_hour, 0);
  const avgRevenuePerOpenDay = toFiniteNumber(row?.avg_revenue_per_open_day, 0);

  if (teamUtilization >= 1.2 && revenuePerHour >= 140) {
    return t("reports:weekdayEconomics.driver.actions.protectCapacity");
  }

  if (teamUtilization >= 1.0 && revenuePerHour < 120) {
    return t("reports:weekdayEconomics.driver.actions.reviewMixPricingStaff");
  }

  if (teamUtilization < 0.35 && avgRevenuePerOpenDay >= 1500) {
    return t("reports:weekdayEconomics.driver.actions.protectPremiumQuality");
  }

  if (teamUtilization < 0.3 && avgRevenuePerOpenDay < 1200) {
    return t("reports:weekdayEconomics.driver.actions.testPromotions");
  }

  return t("reports:weekdayEconomics.driver.actions.monitorWithMix");
}

function getDriverTone(row) {
  const teamUtilization = getTeamUtilizationRatio(row);
  const revenuePerHour = toFiniteNumber(row?.revenue_per_booked_hour, 0);
  const avgRevenuePerOpenDay = toFiniteNumber(row?.avg_revenue_per_open_day, 0);

  if (teamUtilization >= 1.2 && revenuePerHour >= 140) return "power";
  if (teamUtilization >= 0.7 && avgRevenuePerOpenDay >= 1500) return "strong";
  if (teamUtilization < 0.35 && avgRevenuePerOpenDay >= 1500) return "premium";
  if (teamUtilization < 0.3 && avgRevenuePerOpenDay < 1200) return "weak";
  return "balanced";
}

function getDriverToneLabel(tone, t) {
  switch (tone) {
    case "power":
      return t("reports:weekdayEconomics.driver.tones.power");
    case "strong":
      return t("reports:weekdayEconomics.driver.tones.strong");
    case "premium":
      return t("reports:weekdayEconomics.driver.tones.premium");
    case "weak":
      return t("reports:weekdayEconomics.driver.tones.weak");
    case "balanced":
    default:
      return t("reports:weekdayEconomics.driver.tones.balanced");
  }
}

/* -------------------------------------------------------------------------- */
/* Rich tooltip blocks                                                        */
/* -------------------------------------------------------------------------- */

function WeekdayExcellenceTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const row = payload?.[0]?.payload;
  if (!row) return null;

  const singleStreamCoverage = getSingleStreamCoverageRatio(row);

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{row.label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:weekdayEconomics.labels.totalRevenue")}</span>
          <strong>{formatMoney(row.total_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.avgRevenuePerOpenDay")}</span>
          <strong>{formatMoney(row.avg_revenue_per_open_day)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.teamUtilization")}</span>
          <strong>{formatPercentFromRatio(getTeamUtilizationRatio(row))}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.singleStreamCoverage")}</span>
          <strong>
            {singleStreamCoverage == null
              ? t("reports:weekdayEconomics.values.na")
              : formatPercentFromRatio(singleStreamCoverage)}
          </strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.totalVisits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.avgVisitsPerOpenDay")}</span>
          <strong>{formatNumber(row.avg_visits_per_open_day)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.avgBookedMinPerOpenDay")}</span>
          <strong>{formatMinutes(row.avg_booked_minutes_per_open_day)} min</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.executiveClass")}</span>
          <strong>{row.executiveClass}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenueShare")}</span>
          <strong>{formatPercentFromRatio(row.share_of_total_revenue)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {getExecutiveClassExplanation(row.executiveClass, t)}
      </div>
    </div>
  );
}

function WeeklyRhythmTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const row = payload?.[0]?.payload;
  if (!row) return null;

  const openDays = toFiniteNumber(row.open_days_count, 0);
  const openingMinutesPerDay = toFiniteNumber(row.opening_minutes_per_day, 0);
  const totalOpeningMinutesAcrossPeriod = openDays * openingMinutesPerDay;
  const singleStreamCoverage = getSingleStreamCoverageRatio(row);

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:weekdayEconomics.labels.avgRevenuePerOpenDay")}</span>
          <strong>{formatMoney(row.avg_revenue_per_open_day)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.teamUtilization")}</span>
          <strong>{formatPercentFromRatio(getTeamUtilizationRatio(row))}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.singleStreamCoverage")}</span>
          <strong>
            {singleStreamCoverage == null
              ? t("reports:weekdayEconomics.values.na")
              : formatPercentFromRatio(singleStreamCoverage)}
          </strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.avgBookedMinPerOpenDay")}</span>
          <strong>{formatMinutes(row.avg_booked_minutes_per_open_day)} min</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.totalBookedMinutes")}</span>
          <strong>{formatCount(row.total_booked_minutes)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.openDays")}</span>
          <strong>{formatCount(openDays)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.totalOpeningMinutes")}</span>
          <strong>{formatCount(totalOpeningMinutesAcrossPeriod)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {buildWeeklyRhythmInterpretation(row, t)}
      </div>
    </div>
  );
}

function RevenueConcentrationTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenue")}</span>
          <strong>{formatMoney(row.total_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenueShare")}</span>
          <strong>{formatPercentFromRatio(row.share_of_total_revenue)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.cumulativeShare")}</span>
          <strong>{formatPercent(row.cumulative_revenue_share_percent)}</strong>
        </div>
        <div>
          <span>{t("reports:weekdayEconomics.labels.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {t("reports:weekdayEconomics.tooltip.revenueConcentrationExplanation")}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Executive insight helper blocks                                            */
/* -------------------------------------------------------------------------- */

function buildWeeklyRhythmInsight(rows, t) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      headline: t("reports:weekdayEconomics.insights.weeklyRhythm.emptyHeadline"),
      interpretation: t("reports:weekdayEconomics.insights.common.noData"),
      action: t("reports:weekdayEconomics.insights.common.loadValidPeriod"),
    };
  }

  const strongestByAvgDay = [...rows].sort(
    (a, b) =>
      toFiniteNumber(b.avg_revenue_per_open_day, 0) -
        toFiniteNumber(a.avg_revenue_per_open_day, 0) || a.weekday - b.weekday
  )[0];

  const strongestByUtilization = [...rows].sort(
    (a, b) =>
      getTeamUtilizationRatio(b) - getTeamUtilizationRatio(a) ||
      a.weekday - b.weekday
  )[0];

  const weakestByAvgDay = [...rows].sort(
    (a, b) =>
      toFiniteNumber(a.avg_revenue_per_open_day, 0) -
        toFiniteNumber(b.avg_revenue_per_open_day, 0) || a.weekday - b.weekday
  )[0];

  return {
    headline: t("reports:weekdayEconomics.insights.weeklyRhythm.headline", {
      avgDay: strongestByAvgDay?.label || "—",
      utilization: strongestByUtilization?.label || "—",
    }),
    interpretation:
      strongestByAvgDay?.label === strongestByUtilization?.label
        ? t("reports:weekdayEconomics.insights.weeklyRhythm.sameLeader")
        : t("reports:weekdayEconomics.insights.weeklyRhythm.differentLeaders"),
    action: t("reports:weekdayEconomics.insights.weeklyRhythm.action", {
      strongestWeekday:
        strongestByAvgDay?.label ||
        t("reports:weekdayEconomics.insights.weeklyRhythm.strongestFallback"),
      weakestWeekday:
        weakestByAvgDay?.label ||
        t("reports:weekdayEconomics.insights.weeklyRhythm.weakestFallback"),
    }),
  };
}

function buildRevenueConcentrationInsight(paretoRows, t) {
  return {
    headline: t("reports:weekdayEconomics.insights.concentration.headline"),
    interpretation: buildRevenueConcentrationInterpretation(paretoRows, t),
    action: t("reports:weekdayEconomics.insights.concentration.action"),
  };
}

function buildExcellenceMatrixInsight(rows, t) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      headline: t("reports:weekdayEconomics.insights.excellence.emptyHeadline"),
      interpretation: t("reports:weekdayEconomics.insights.common.noData"),
      action: t("reports:weekdayEconomics.insights.common.loadValidPeriod"),
    };
  }

  const stars = rows.filter((row) => row.executiveClass === "star");
  const weaks = rows.filter((row) => row.executiveClass === "weak");

  if (stars.length > 0 && weaks.length > 0) {
    return {
      headline: t("reports:weekdayEconomics.insights.excellence.starWeakHeadline", {
        starWeekday: stars[0].label,
        weakWeekday: weaks[0].label,
      }),
      interpretation: t("reports:weekdayEconomics.insights.excellence.starWeakInterpretation"),
      action: t("reports:weekdayEconomics.insights.excellence.starWeakAction"),
    };
  }

  if (stars.length > 0) {
    return {
      headline: t("reports:weekdayEconomics.insights.excellence.starHeadline", {
        weekday: stars[0].label,
      }),
      interpretation: t("reports:weekdayEconomics.insights.excellence.starInterpretation"),
      action: t("reports:weekdayEconomics.insights.excellence.starAction"),
    };
  }

  return {
    headline: t("reports:weekdayEconomics.insights.excellence.balancedHeadline"),
    interpretation: t("reports:weekdayEconomics.insights.excellence.balancedInterpretation"),
    action: t("reports:weekdayEconomics.insights.excellence.balancedAction"),
  };
}

/* -------------------------------------------------------------------------- */
/* Driver drilldown panel                                                     */
/* -------------------------------------------------------------------------- */

function WeekdayDriverPanel({
  row,
  onSelectPrev,
  onSelectNext,
  hasPrev,
  hasNext,
  t,
}) {
  if (!row) {
    return (
      <div className="driver-panel driver-panel--empty">
        <div className="driver-panel__title">{t("reports:weekdayEconomics.driver.cardTitle")}</div>
        <div className="driver-panel__empty">
          {t("reports:weekdayEconomics.driver.emptyText")}
        </div>
      </div>
    );
  }

  const tone = getDriverTone(row);
  const toneLabel = getDriverToneLabel(tone, t);

  const openDays = toFiniteNumber(row.open_days_count, 0);
  const openingMinutesPerDay = toFiniteNumber(row.opening_minutes_per_day, 0);
  const totalOpeningMinutesAcrossPeriod = openDays * openingMinutesPerDay;
  const totalBookedMinutes = toFiniteNumber(row.total_booked_minutes, 0);
  const totalRevenue = toFiniteNumber(row.total_revenue, 0);
  const singleStreamCoverage = getSingleStreamCoverageRatio(row);

  return (
    <div className={`driver-panel driver-panel--${tone}`}>
      <div className="driver-panel__topbar">
        <div>
          <div className="driver-panel__eyebrow">{t("reports:weekdayEconomics.driver.eyebrow")}</div>
          <div className="driver-panel__title">{row.label}</div>
        </div>

        <div className="driver-panel__actions">
          <span className={`driver-tone-badge driver-tone-badge--${tone}`}>
            {toneLabel}
          </span>

          <button
            type="button"
            className="driver-nav-button"
            onClick={onSelectPrev}
            disabled={!hasPrev}
          >
            ← {t("reports:weekdayEconomics.driver.prev")}
          </button>

          <button
            type="button"
            className="driver-nav-button"
            onClick={onSelectNext}
            disabled={!hasNext}
          >
            {t("reports:weekdayEconomics.driver.next")} →
          </button>
        </div>
      </div>

      <div className="driver-panel__headline">{buildUtilizationHeadline(row, t)}</div>

      <div className="driver-panel__subline">
        {t("reports:weekdayEconomics.driver.subline")}
      </div>

      <div className="driver-grid">
        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.bookedMinutes")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(totalBookedMinutes)}
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.openingMinutes")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(totalOpeningMinutesAcrossPeriod)}
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.visits")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(row.total_visits)}
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.avgRevenuePerOpenDay")}</span>
          <strong className="driver-metric-card__value">
            {formatMoney(row.avg_revenue_per_open_day)} PLN
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.revenuePerBookedHour")}</span>
          <strong className="driver-metric-card__value">
            {formatMoney(row.revenue_per_booked_hour)} PLN/h
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:weekdayEconomics.labels.avgVisitsPerOpenDay")}</span>
          <strong className="driver-metric-card__value">
            {formatNumber(row.avg_visits_per_open_day)}
          </strong>
        </div>
      </div>

      <div className="driver-breakdown-grid">
        <div className="driver-breakdown-card">
          <div className="driver-breakdown-card__title">{t("reports:weekdayEconomics.driver.formulaDecomposition")}</div>
          <div className="driver-breakdown-card__text">
            <div>
              <strong>{t("reports:weekdayEconomics.labels.teamUtilization")}:</strong>{" "}
              {formatPercentFromRatio(getTeamUtilizationRatio(row))} ={" "}
              <strong>{formatCount(totalBookedMinutes)}</strong>{" "}
              {t("reports:weekdayEconomics.driver.bookedMin")} ÷{" "}
              <strong>{formatCount(totalOpeningMinutesAcrossPeriod)}</strong>{" "}
              {t("reports:weekdayEconomics.driver.openingMin")}
            </div>

            <div style={{ marginTop: 8 }}>
              <strong>{t("reports:weekdayEconomics.labels.singleStreamCoverage")}:</strong>{" "}
              {singleStreamCoverage == null
                ? t("reports:weekdayEconomics.values.na")
                : formatPercentFromRatio(singleStreamCoverage)}{" "}
              = {t("reports:weekdayEconomics.driver.singleStreamFormulaSuffix")}
            </div>
          </div>
        </div>

        <div className="driver-breakdown-card">
          <div className="driver-breakdown-card__title">{t("reports:weekdayEconomics.driver.businessMeaning")}</div>
          <div className="driver-breakdown-card__text">
            {buildUtilizationMeaning(row, t)}
          </div>
        </div>

        <div className="driver-breakdown-card driver-breakdown-card--action">
          <div className="driver-breakdown-card__title">{t("reports:weekdayEconomics.driver.suggestedAction")}</div>
          <div className="driver-breakdown-card__text">
            {buildUtilizationAction(row, t)}
          </div>
        </div>
      </div>

      <div className="driver-footer-grid">
        <div className="driver-footer-item">
          <span>{t("reports:weekdayEconomics.labels.singleStreamCoverage")}</span>
          <strong>
            {singleStreamCoverage == null
              ? t("reports:weekdayEconomics.values.na")
              : formatPercentFromRatio(singleStreamCoverage)}
          </strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:weekdayEconomics.labels.openDays")}</span>
          <strong>{formatCount(openDays)}</strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:weekdayEconomics.labels.openingMinPerDay")}</span>
          <strong>{formatCount(openingMinutesPerDay)}</strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:weekdayEconomics.labels.totalRevenue")}</span>
          <strong>{formatMoney(totalRevenue)} PLN</strong>
        </div>
      </div>

      <div className="driver-footer-grid">
        <div className="driver-footer-item">
          <span>{buildSingleStreamCoverageHeadline(row, t)}</span>
          <strong>
            {singleStreamCoverage == null
              ? t("reports:weekdayEconomics.values.notAvailable")
              : t("reports:weekdayEconomics.driver.keyAnchorText")}
          </strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:weekdayEconomics.labels.revenueShare")}</span>
          <strong>{formatPercentFromRatio(row.share_of_total_revenue)}</strong>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const WeekdayEconomicsReportPage = () => {
  const { t } = useTranslation(["reports", "common"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Evaluation mode policy mirrors the other report pages.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Page-local filters.
   *
   * Only userIds is backend-facing.
   * Search and sorting stay local for speed and clarity.
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    searchText: "",
    sortBy: "total_revenue",
    sortDir: "desc",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Tracks the period actually loaded from backend.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /**
   * Selected weekday for executive drilldown.
   */
  const [selectedWeekdayId, setSelectedWeekdayId] = useState(null);

  /* ---------------------------------------------------------------------- */
  /* Keep userIds aligned for role === user                                 */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ---------------------------------------------------------------------- */
  /* Load users for employee filter                                         */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[WeekdayEconomicsReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:weekdayEconomics.errors.failedLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ---------------------------------------------------------------------- */
  /* Current user display helper                                            */
  /* ---------------------------------------------------------------------- */

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser");
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ---------------------------------------------------------------------- */
  /* Fetch report                                                           */
  /* ---------------------------------------------------------------------- */

  const loadReportByRange = useCallback(async ({ from, to, userIds, displayLabel }) => {
    if (!from || !to) {
      setError(t("reports:common.invalidEvaluationPeriod"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = await fetchWeekdayEconomicsReport(from, to, userIds);
      setReport(payload || null);

      setLoadedPeriodMeta({
        from,
        to,
        displayLabel: displayLabel || "",
      });
    } catch (err) {
      console.error("[WeekdayEconomicsReportPage] ❌ Error fetching report:", err);
      setError(t("reports:weekdayEconomics.errors.failedFetch"));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Stable initial request prevents drift during first render lifecycle.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ---------------------------------------------------------------------- */
  /* Derived data                                                           */
  /* ---------------------------------------------------------------------- */

  const rows = useMemo(() => {
    return Array.isArray(report?.rows) ? report.rows : [];
  }, [report]);

  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "weekday_economics";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const summary = report?.summary || {
    analyzed_days_total: 0,
    open_days_total: 0,
    completed_visits_count: 0,
    total_revenue: "0.00",
    total_booked_minutes: 0,
  };

  /* ---------------------------------------------------------------------- */
  /* Local search + sorting                                                 */
  /* ---------------------------------------------------------------------- */

  const filteredRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return rows;

    return rows.filter((row) => {
      const weekday = String(row.weekday_label || "").toLowerCase();
      const topCategories = formatTopList(row.top_categories, t).toLowerCase();
      const topServices = formatTopList(row.top_services, t).toLowerCase();

      return (
        weekday.includes(searchText) ||
        topCategories.includes(searchText) ||
        topServices.includes(searchText)
      );
    });
  }, [rows, filters.searchText, t]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    const sortBy = filters.sortBy || "total_revenue";
    const sortDir = filters.sortDir === "asc" ? "asc" : "desc";

    out.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case "weekday_label":
          cmp = String(a.weekday_label || "").localeCompare(
            String(b.weekday_label || "")
          );
          break;

        case "avg_revenue_per_open_day":
          cmp =
            toFiniteNumber(a.avg_revenue_per_open_day, 0) -
            toFiniteNumber(b.avg_revenue_per_open_day, 0);
          break;

        case "revenue_per_booked_hour":
          cmp =
            toFiniteNumber(a.revenue_per_booked_hour, 0) -
            toFiniteNumber(b.revenue_per_booked_hour, 0);
          break;

        case "team_utilization_vs_opening_time":
          cmp = getTeamUtilizationRatio(a) - getTeamUtilizationRatio(b);
          break;

        case "single_stream_opening_coverage":
          cmp =
            toFiniteNumber(a.single_stream_opening_coverage, -1) -
            toFiniteNumber(b.single_stream_opening_coverage, -1);
          break;

        case "total_visits":
          cmp = toFiniteNumber(a.total_visits, 0) - toFiniteNumber(b.total_visits, 0);
          break;

        case "total_booked_minutes":
          cmp =
            toFiniteNumber(a.total_booked_minutes, 0) -
            toFiniteNumber(b.total_booked_minutes, 0);
          break;

        case "total_revenue":
        default:
          cmp =
            toFiniteNumber(a.total_revenue, 0) - toFiniteNumber(b.total_revenue, 0);
          break;
      }

      if (cmp === 0) {
        cmp = toFiniteNumber(a.weekday, 0) - toFiniteNumber(b.weekday, 0);
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return out;
  }, [filteredRows, filters.sortBy, filters.sortDir]);

  /* ---------------------------------------------------------------------- */
  /* Highlight cards                                                        */
  /* ---------------------------------------------------------------------- */

  const topRevenueWeekday = summary?.strongest_revenue_weekday || null;
  const topRevenuePerOpenDayWeekday =
    summary?.strongest_revenue_per_open_day_weekday || null;
  const topHourlyWeekday = summary?.strongest_hourly_weekday || null;

  /* ---------------------------------------------------------------------- */
  /* Row normalization                                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * Normalize rows into chart-safe structures.
   *
   * We normalize once here so the rest of the page can work with consistent
   * numeric data and stable ids.
   */
  const weekdayChartRows = useMemo(() => {
    return rows.map((row, index) => ({
      ...row,
      id: `${row?.weekday_label || "weekday"}-${index}`,
      label: row?.weekday_label || t("reports:common.unknown"),

      weekday: toFiniteNumber(row?.weekday, 0),
      open_days_count: toFiniteNumber(row?.open_days_count, 0),
      opening_minutes_per_day: toFiniteNumber(row?.opening_minutes_per_day, 0),
      theoretical_open_minutes_in_period: toFiniteNumber(
        row?.theoretical_open_minutes_in_period,
        0
      ),

      total_revenue: toFiniteNumber(row?.total_revenue, 0),
      total_booked_minutes: toFiniteNumber(row?.total_booked_minutes, 0),
      total_visits: toFiniteNumber(row?.total_visits, 0),

      avg_revenue_per_open_day: toFiniteNumber(row?.avg_revenue_per_open_day, 0),
      avg_booked_minutes_per_open_day: toFiniteNumber(
        row?.avg_booked_minutes_per_open_day,
        0
      ),
      avg_revenue_per_visit: toFiniteNumber(row?.avg_revenue_per_visit, 0),
      avg_visits_per_open_day: toFiniteNumber(row?.avg_visits_per_open_day, 0),
      avg_duration_minutes: toFiniteNumber(row?.avg_duration_minutes, 0),

      revenue_per_booked_hour: toFiniteNumber(row?.revenue_per_booked_hour, 0),

      team_utilization_vs_opening_time: toFiniteNumber(
        row?.team_utilization_vs_opening_time,
        0
      ),
      single_stream_opening_coverage:
        row?.single_stream_opening_coverage == null
          ? null
          : toFiniteNumber(row?.single_stream_opening_coverage, 0),

      share_of_total_revenue: toFiniteNumber(row?.share_of_total_revenue, 0),
      share_of_total_booked_minutes: toFiniteNumber(
        row?.share_of_total_booked_minutes,
        0
      ),
      share_of_total_visits: toFiniteNumber(row?.share_of_total_visits, 0),
    }));
  }, [rows, t]);

  /**
   * Stable lookup map used by the ranking table.
   */
  const weekdayChartRowMap = useMemo(() => {
    const map = new Map();
    weekdayChartRows.forEach((row) => {
      map.set(String(row.weekday), row);
    });
    return map;
  }, [weekdayChartRows]);

  /* ---------------------------------------------------------------------- */
  /* Executive chart datasets                                               */
  /* ---------------------------------------------------------------------- */

  /**
   * Weekday Excellence Matrix
   *
   * X = total revenue
   * Y = revenue per booked hour
   * bubble size = team utilization vs opening time
   */
  const weekdayExcellenceMatrixData = useMemo(() => {
    if (!weekdayChartRows.length) return [];

    const utilizationValues = weekdayChartRows.map(
      (row) => getTeamUtilizationRatio(row) * 100
    );
    const minUtilization = Math.min(...utilizationValues);
    const maxUtilization = Math.max(...utilizationValues);

    return weekdayChartRows.map((row) => {
      const executiveClass = getWeekdayExecutiveClass(row);

      return {
        ...row,
        x: row.total_revenue,
        y: row.revenue_per_booked_hour,
        z: bubbleSizeFromValue(
          getTeamUtilizationRatio(row) * 100,
          minUtilization,
          maxUtilization
        ),
        executiveClass,
        color: getWeekdayExecutiveColor(executiveClass),
      };
    });
  }, [weekdayChartRows]);

  /**
   * Natural weekday order dataset.
   */
  const weeklyRhythmData = useMemo(() => {
    return [...weekdayChartRows].sort((a, b) => a.weekday - b.weekday);
  }, [weekdayChartRows]);

  /**
   * Pareto dataset sorted by revenue descending.
   */
  const weekdayParetoData = useMemo(() => {
    const sorted = [...weekdayChartRows].sort(
      (a, b) =>
        toFiniteNumber(b.total_revenue, 0) - toFiniteNumber(a.total_revenue, 0) ||
        a.weekday - b.weekday
    );

    const totalRevenue = sorted.reduce(
      (sum, row) => sum + toFiniteNumber(row.total_revenue, 0),
      0
    );

    let runningRevenue = 0;

    return sorted.map((row) => {
      runningRevenue += toFiniteNumber(row.total_revenue, 0);

      return {
        ...row,
        cumulative_revenue_share_percent:
          totalRevenue > 0 ? (runningRevenue / totalRevenue) * 100 : 0,
      };
    });
  }, [weekdayChartRows]);

  const weekdayRevenueVsFillData = useMemo(() => {
    return [...weekdayChartRows].sort((a, b) => a.weekday - b.weekday);
  }, [weekdayChartRows]);

  /* ---------------------------------------------------------------------- */
  /* Executive insights                                                     */
  /* ---------------------------------------------------------------------- */

  const excellenceInsight = useMemo(() => {
    return buildExcellenceMatrixInsight(weekdayExcellenceMatrixData, t);
  }, [weekdayExcellenceMatrixData, t]);

  const weeklyRhythmInsight = useMemo(() => {
    return buildWeeklyRhythmInsight(weeklyRhythmData, t);
  }, [weeklyRhythmData, t]);

  const concentrationInsight = useMemo(() => {
    return buildRevenueConcentrationInsight(weekdayParetoData, t);
  }, [weekdayParetoData, t]);

  /* ---------------------------------------------------------------------- */
  /* Driver panel selection logic                                           */
  /* ---------------------------------------------------------------------- */

  const drilldownRows = useMemo(() => {
    return [...weekdayChartRows].sort((a, b) => a.weekday - b.weekday);
  }, [weekdayChartRows]);

  /**
   * Keep selection valid when data changes.
   * Default selection = strongest weekday by total revenue.
   */
  useEffect(() => {
    if (!drilldownRows.length) {
      setSelectedWeekdayId(null);
      return;
    }

    const exists = drilldownRows.some((row) => row.id === selectedWeekdayId);
    if (exists) return;

    const strongest = [...drilldownRows].sort(
      (a, b) =>
        toFiniteNumber(b.total_revenue, 0) - toFiniteNumber(a.total_revenue, 0) ||
        a.weekday - b.weekday
    )[0];

    setSelectedWeekdayId(strongest?.id || drilldownRows[0]?.id || null);
  }, [drilldownRows, selectedWeekdayId]);

  const selectedWeekdayRow = useMemo(() => {
    return drilldownRows.find((row) => row.id === selectedWeekdayId) || null;
  }, [drilldownRows, selectedWeekdayId]);

  const selectedWeekdayIndex = useMemo(() => {
    return drilldownRows.findIndex((row) => row.id === selectedWeekdayId);
  }, [drilldownRows, selectedWeekdayId]);

  const canSelectPrev = selectedWeekdayIndex > 0;
  const canSelectNext =
    selectedWeekdayIndex >= 0 && selectedWeekdayIndex < drilldownRows.length - 1;

  const handleSelectPrevWeekday = useCallback(() => {
    if (!canSelectPrev) return;
    setSelectedWeekdayId(drilldownRows[selectedWeekdayIndex - 1]?.id || null);
  }, [canSelectPrev, drilldownRows, selectedWeekdayIndex]);

  const handleSelectNextWeekday = useCallback(() => {
    if (!canSelectNext) return;
    setSelectedWeekdayId(drilldownRows[selectedWeekdayIndex + 1]?.id || null);
  }, [canSelectNext, drilldownRows, selectedWeekdayIndex]);

  const handleSelectWeekdayRow = useCallback((row) => {
    if (!row?.id) return;
    setSelectedWeekdayId(row.id);
  }, []);

  /**
   * Recharts click payload shape differs slightly between chart types.
   */
  const resolveClickedChartRow = useCallback((chartState) => {
    if (!chartState) return null;

    if (chartState?.activePayload?.[0]?.payload) {
      return chartState.activePayload[0].payload;
    }

    if (chartState?.payload) {
      return chartState.payload;
    }

    return null;
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="weekday-economics-page">
      <div className="weekday-economics-header">
        <div className="header-left">
          <h1>{t("reports:weekdayEconomics.title")}</h1>
          <p className="header-hint">
            {t("reports:weekdayEconomics.subtitle")}
          </p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:weekdayEconomics.filters.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-sort">
            <label className="filter-label">{t("reports:weekdayEconomics.filters.sortBy")}</label>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: e.target.value,
                }))
              }
            >
              <option value="total_revenue">{t("reports:weekdayEconomics.labels.totalRevenue")}</option>
              <option value="avg_revenue_per_open_day">{t("reports:weekdayEconomics.labels.avgRevenuePerOpenDay")}</option>
              <option value="revenue_per_booked_hour">{t("reports:weekdayEconomics.labels.revenuePerBookedHour")}</option>
              <option value="team_utilization_vs_opening_time">
                {t("reports:weekdayEconomics.labels.teamUtilization")}
              </option>
              <option value="single_stream_opening_coverage">
                {t("reports:weekdayEconomics.labels.singleStreamCoverage")}
              </option>
              <option value="total_visits">{t("reports:weekdayEconomics.labels.totalVisits")}</option>
              <option value="total_booked_minutes">{t("reports:weekdayEconomics.labels.totalBookedMinutes")}</option>
              <option value="weekday_label">{t("reports:weekdayEconomics.labels.weekday")}</option>
            </select>
          </div>

          <div className="filter-item filter-item-sortdir">
            <label className="filter-label">{t("reports:weekdayEconomics.filters.direction")}</label>
            <select
              className="sort-select"
              value={filters.sortDir}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortDir: e.target.value,
                }))
              }
            >
              <option value="desc">{t("reports:weekdayEconomics.filters.descending")}</option>
              <option value="asc">{t("reports:weekdayEconomics.filters.ascending")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : report ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:weekdayEconomics.summary.analyzedDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.analyzed_days_total)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:weekdayEconomics.labels.openDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.open_days_total)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:weekdayEconomics.summary.completedVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.completed_visits_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:weekdayEconomics.labels.totalRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:weekdayEconomics.labels.totalBookedMinutes")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_booked_minutes)}
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:weekdayEconomics.highlights.topByRevenue")}</div>
                {topRevenueWeekday ? (
                  <>
                    <div className="highlight-name">
                      {topRevenueWeekday.weekday_label}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(topRevenueWeekday.total_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:weekdayEconomics.highlights.topByRevenuePerOpenDay")}</div>
                {topRevenuePerOpenDayWeekday ? (
                  <>
                    <div className="highlight-name">
                      {topRevenuePerOpenDayWeekday.weekday_label}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(topRevenuePerOpenDayWeekday.avg_revenue_per_open_day)} PLN/day
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:weekdayEconomics.highlights.topByRevenuePerBookedHour")}</div>
                {topHourlyWeekday ? (
                  <>
                    <div className="highlight-name">
                      {topHourlyWeekday.weekday_label}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(topHourlyWeekday.revenue_per_booked_hour)} PLN/h
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:weekdayEconomics.methodology.title")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:weekdayEconomics.charts.excellence.title")}</h2>
                  <p>
                    {t("reports:weekdayEconomics.charts.excellence.description")}
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 10, right: 24, bottom: 12, left: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWeekdayRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:weekdayEconomics.labels.totalRevenue")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value)}`}
                        label={{
                          value: t("reports:weekdayEconomics.axis.totalRevenuePln"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:weekdayEconomics.labels.revenuePerBookedHour")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:weekdayEconomics.axis.revenuePerBookedHourPln"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[90, 340]} />
                      <Tooltip content={<WeekdayExcellenceTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        y={120}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:weekdayEconomics.chartSeries.weekdays")} data={weekdayExcellenceMatrixData}>
                        {weekdayExcellenceMatrixData.map((entry) => (
                          <Cell
                            key={`weekday-excellence-${entry.id}`}
                            fill={entry.color}
                            stroke={
                              entry.id === selectedWeekdayId
                                ? "#111827"
                                : "transparent"
                            }
                            strokeWidth={entry.id === selectedWeekdayId ? 2 : 0}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--star">{t("reports:weekdayEconomics.executiveClasses.star")}</span>
                  <span className="legend-chip legend-chip--efficient">{t("reports:weekdayEconomics.executiveClasses.efficient")}</span>
                  <span className="legend-chip legend-chip--volume">{t("reports:weekdayEconomics.executiveClasses.volume")}</span>
                  <span className="legend-chip legend-chip--weak">{t("reports:weekdayEconomics.executiveClasses.weak")}</span>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{excellenceInsight.headline}</div>
                  <div className="insight-card__text">{excellenceInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:weekdayEconomics.insights.common.suggestedAction")}:</strong> {excellenceInsight.action}
                  </div>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:weekdayEconomics.charts.weeklyRhythm.title")}</h2>
                  <p>
                    {t("reports:weekdayEconomics.charts.weeklyRhythm.description")}
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weeklyRhythmData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWeekdayRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />

                      <Tooltip content={<WeeklyRhythmTooltip t={t} />} />
                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="avg_revenue_per_open_day"
                        name={t("reports:weekdayEconomics.labels.avgRevenuePerOpenDay")}
                        fill={CHART_COLORS.revenue}
                        radius={[6, 6, 0, 0]}
                      >
                        {weeklyRhythmData.map((entry) => (
                          <Cell
                            key={`weekly-rhythm-bar-${entry.id}`}
                            fill={CHART_COLORS.revenue}
                            stroke={
                              entry.id === selectedWeekdayId
                                ? "#111827"
                                : "transparent"
                            }
                            strokeWidth={entry.id === selectedWeekdayId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="team_utilization_vs_opening_time"
                        name={t("reports:weekdayEconomics.labels.teamUtilization")}
                        stroke={CHART_COLORS.fill}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.fill }}
                        activeDot={{ r: 5, fill: CHART_COLORS.fill }}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{weeklyRhythmInsight.headline}</div>
                  <div className="insight-card__text">{weeklyRhythmInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:weekdayEconomics.insights.common.suggestedAction")}:</strong> {weeklyRhythmInsight.action}
                  </div>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:weekdayEconomics.charts.revenueVsUtilization.title")}</h2>
                  <p>
                    {t("reports:weekdayEconomics.charts.revenueVsUtilization.description")}
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdayRevenueVsFillData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWeekdayRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />

                      <Tooltip content={<WeeklyRhythmTooltip t={t} />} />
                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="total_revenue"
                        name={t("reports:weekdayEconomics.labels.revenue")}
                        fill={CHART_COLORS.revenueLight}
                        radius={[6, 6, 0, 0]}
                      >
                        {weekdayRevenueVsFillData.map((entry) => (
                          <Cell
                            key={`revenue-vs-fill-bar-${entry.id}`}
                            fill={CHART_COLORS.revenueLight}
                            stroke={
                              entry.id === selectedWeekdayId
                                ? "#111827"
                                : "transparent"
                            }
                            strokeWidth={entry.id === selectedWeekdayId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="team_utilization_vs_opening_time"
                        name={t("reports:weekdayEconomics.labels.teamUtilization")}
                        stroke={CHART_COLORS.efficiency}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.efficiency }}
                        activeDot={{ r: 5, fill: CHART_COLORS.efficiency }}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:weekdayEconomics.charts.revenueConcentration.title")}</h2>
                  <p>
                    {t("reports:weekdayEconomics.charts.revenueConcentration.description")}
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdayParetoData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWeekdayRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${value}%`}
                      />

                      <Tooltip content={<RevenueConcentrationTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        yAxisId="right"
                        y={80}
                        stroke="#94a3b8"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Bar
                        yAxisId="left"
                        dataKey="total_revenue"
                        name={t("reports:weekdayEconomics.labels.revenue")}
                        fill={CHART_COLORS.revenue}
                        radius={[6, 6, 0, 0]}
                      >
                        {weekdayParetoData.map((entry) => (
                          <Cell
                            key={`weekday-pareto-bar-${entry.id}`}
                            fill={CHART_COLORS.revenue}
                            stroke={
                              entry.id === selectedWeekdayId
                                ? "#111827"
                                : "transparent"
                            }
                            strokeWidth={entry.id === selectedWeekdayId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_revenue_share_percent"
                        name={t("reports:weekdayEconomics.labels.cumulativeShare")}
                        stroke={CHART_COLORS.cumulative}
                        strokeWidth={2}
                        dot={{ r: 2, fill: CHART_COLORS.cumulative }}
                        activeDot={{ r: 5, fill: CHART_COLORS.cumulative }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{concentrationInsight.headline}</div>
                  <div className="insight-card__text">{concentrationInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:weekdayEconomics.insights.common.suggestedAction")}:</strong> {concentrationInsight.action}
                  </div>
                </div>
              </div>
            </div>

            <WeekdayDriverPanel
              row={selectedWeekdayRow}
              onSelectPrev={handleSelectPrevWeekday}
              onSelectNext={handleSelectNextWeekday}
              hasPrev={canSelectPrev}
              hasNext={canSelectNext}
              t={t}
            />

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:weekdayEconomics.table.title")}</h2>
                <p>
                  {t("reports:weekdayEconomics.table.description")}
                </p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:weekdayEconomics.labels.weekday")}</th>
                      <th>{t("reports:weekdayEconomics.labels.openDays")}</th>
                      <th>{t("reports:weekdayEconomics.labels.openingMinPerDay")}</th>
                      <th>{t("reports:weekdayEconomics.labels.totalVisits")}</th>
                      <th>{t("reports:weekdayEconomics.labels.totalBookedMin")}</th>
                      <th>{t("reports:weekdayEconomics.labels.totalRevenuePln")}</th>
                      <th>{t("reports:weekdayEconomics.labels.avgRevenuePerVisitPln")}</th>
                      <th>{t("reports:weekdayEconomics.labels.avgRevenuePerOpenDayPln")}</th>
                      <th>{t("reports:weekdayEconomics.labels.avgBookedMinPerOpenDay")}</th>
                      <th>{t("reports:weekdayEconomics.labels.avgVisitsPerOpenDay")}</th>
                      <th>{t("reports:weekdayEconomics.labels.revenuePerBookedHourPln")}</th>
                      <th>{t("reports:weekdayEconomics.labels.teamUtilization")}</th>
                      <th>{t("reports:weekdayEconomics.labels.singleStreamCoverage")}</th>
                      <th>{t("reports:weekdayEconomics.labels.topCategories")}</th>
                      <th>{t("reports:weekdayEconomics.labels.topServices")}</th>
                      <th>{t("reports:weekdayEconomics.labels.revenueShare")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length > 0 ? (
                      sortedRows.map((row) => {
                        const normalizedRow =
                          weekdayChartRowMap.get(String(row.weekday)) || null;

                        const isSelected = normalizedRow?.id === selectedWeekdayId;

                        return (
                          <tr
                            key={row.weekday}
                            className={isSelected ? "table-row-selected" : ""}
                            onClick={() =>
                              normalizedRow && handleSelectWeekdayRow(normalizedRow)
                            }
                          >
                            <td>{row.weekday_label}</td>
                            <td>{formatCount(row.open_days_count)}</td>
                            <td>{formatCount(row.opening_minutes_per_day)}</td>
                            <td>{formatCount(row.total_visits)}</td>
                            <td>{formatCount(row.total_booked_minutes)}</td>
                            <td>{formatMoney(row.total_revenue)}</td>
                            <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                            <td>{formatMoney(row.avg_revenue_per_open_day)}</td>
                            <td>{formatMinutes(row.avg_booked_minutes_per_open_day)}</td>
                            <td>{formatNumber(row.avg_visits_per_open_day)}</td>
                            <td>{formatMoney(row.revenue_per_booked_hour)}</td>
                            <td>{formatPercentFromRatio(getTeamUtilizationRatio(row))}</td>
                            <td>
                              {getSingleStreamCoverageRatio(row) == null
                                ? t("reports:weekdayEconomics.values.na")
                                : formatPercentFromRatio(
                                    getSingleStreamCoverageRatio(row)
                                  )}
                            </td>
                            <td className="wrap-cell">
                              {formatTopList(row.top_categories, t)}
                            </td>
                            <td className="wrap-cell">
                              {formatTopList(row.top_services, t)}
                            </td>
                            <td>{formatPercentFromRatio(row.share_of_total_revenue)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={16} className="empty-table-cell">
                          {t("reports:weekdayEconomics.table.noMatchingWeekdays")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default WeekdayEconomicsReportPage;

